import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  BarChart3, Link2, PenSquare, CalendarDays, Layers, Rocket, CalendarRange,
  Plus, Send, Save, Eye, Trash2, ChevronLeft, ChevronRight, RefreshCw,
  Users, FileText, Zap, Clock, ExternalLink, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import SocialConnectionsWidget from "@/components/SocialConnectionsWidget";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

/* ── Platform config ── */
const P = {
  facebook:        { label: "Facebook",        color: "#1877F2", short: "FB" },
  instagram:       { label: "Instagram",       color: "#E4405F", short: "IG" },
  linkedin:        { label: "LinkedIn",        color: "#0A66C2", short: "LI" },
  twitter:         { label: "X (Twitter)",     color: "#1DA1F2", short: "X" },
  google_business: { label: "Google Business", color: "#4285F4", short: "G" },
  tiktok:          { label: "TikTok",          color: "#010101", short: "TT" },
};
const ALL_PLATFORMS = Object.keys(P);

const STATUS_COLORS = {
  draft: "bg-[#2A3441] text-[#94A3B8]",
  pending_approval: "bg-yellow-500/15 text-yellow-400",
  approved: "bg-green-500/15 text-green-400",
  published: "bg-[#0099CC]/15 text-[#0099CC]",
};

const STATUS_LABELS = {
  draft: "Brouillon", pending_approval: "A valider",
  approved: "Approuve", published: "Publie",
};

/* ── Sidebar Tabs ── */
const TABS = [
  { key: "dashboard",    label: "Dashboard",    icon: BarChart3 },
  { key: "connections",  label: "Connexions",   icon: Link2 },
  { key: "create",       label: "Publication",  icon: PenSquare },
  { key: "event",        label: "Evenement",    icon: CalendarDays },
  { key: "series",       label: "Serie",        icon: Layers },
  { key: "boost",        label: "Boost",        icon: Rocket },
  { key: "planner",      label: "Planificateur", icon: CalendarRange },
];

export default function SocialMediaPage() {
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("dashboard");
  const [clients, setClients] = useState([]);
  const [selClient, setSelClient] = useState("");
  const isAdmin = user?.role === "admin";
  const headers = { Authorization: `Bearer ${token}` };

  // Handle Facebook OAuth redirect
  useEffect(() => {
    const fbSuccess = searchParams.get("fb_success");
    const fbError = searchParams.get("fb_error");
    if (fbSuccess) {
      toast.success("Facebook/Instagram connecte avec succes !");
      setTab("connections");
      setSearchParams({});
    } else if (fbError) {
      toast.error(`Erreur connexion Facebook: ${fbError}`);
      setTab("connections");
      setSearchParams({});
    }
  }, [searchParams]);

  useEffect(() => {
    if (isAdmin) {
      axios.get(`${API}/clients`, { headers }).then(r => {
        setClients(r.data);
        if (r.data.length) setSelClient(r.data[0].id);
      }).catch(() => {});
    }
  }, []);

  const clientId = isAdmin ? selClient : user?.client_id;

  return (
    <div className="flex gap-6 min-h-[calc(100vh-120px)]" data-testid="social-media-page">
      {/* Left sidebar */}
      <div className="w-52 flex-shrink-0 space-y-1" data-testid="social-sidebar">
        {isAdmin && clients.length > 0 && (
          <select value={selClient} onChange={e => setSelClient(e.target.value)}
            className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-xs mb-3"
            data-testid="social-client-selector">
            {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        )}
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              tab === t.key ? "bg-[#0099CC]/15 text-[#0099CC]" : "text-[#94A3B8] hover:bg-[#151B2B] hover:text-white"
            }`} data-testid={`social-tab-${t.key}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        {tab === "dashboard" && <DashboardTab clientId={clientId} headers={headers} />}
        {tab === "connections" && <SocialConnectionsWidget clientId={clientId} readOnly={!isAdmin} />}
        {tab === "create" && <CreatePostTab clientId={clientId} headers={headers} isAdmin={isAdmin} />}
        {tab === "event" && <CreateEventTab clientId={clientId} headers={headers} />}
        {tab === "series" && <SeriesTab clientId={clientId} headers={headers} />}
        {tab === "boost" && <BoostTab clientId={clientId} headers={headers} />}
        {tab === "planner" && <PlannerTab clientId={clientId} headers={headers} />}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════
   A) DASHBOARD TAB
   ═══════════════════════════════════════════════════════ */
function DashboardTab({ clientId, headers }) {
  const [stats, setStats] = useState(null);
  const [cal, setCal] = useState([]);

  const fetch = useCallback(async () => {
    if (!clientId) return;
    const [s, c] = await Promise.all([
      axios.get(`${API}/social/dashboard`, { headers, params: { client_id: clientId } }),
      axios.get(`${API}/social/calendar`, { headers, params: { client_id: clientId } }),
    ]);
    setStats(s.data);
    setCal(c.data);
  }, [clientId]);

  useEffect(() => { fetch(); }, [fetch]);

  if (!stats) return <div className="text-[#64748b] text-center py-12">Chargement...</div>;

  const cards = [
    { label: "Publications",      val: stats.published,       icon: FileText, color: "border-[#0099CC]" },
    { label: "Brouillons",        val: stats.drafts,          icon: Save,     color: "border-[#94A3B8]" },
    { label: "Planifies",         val: stats.scheduled,       icon: Clock,    color: "border-yellow-500" },
    { label: "Evenements",        val: stats.events,          icon: CalendarDays, color: "border-purple-500" },
    { label: "Boostes",           val: stats.boosted,         icon: Rocket,   color: "border-red-500" },
    { label: "Total Abonnes",     val: stats.total_followers, icon: Users,    color: "border-green-500" },
  ];

  // Weekly view: group calendar items by day of week
  const now = new Date();
  const dayNames = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay() + 1);

  return (
    <div className="space-y-6" data-testid="social-dashboard-tab">
      <h2 className="text-xl font-bold text-white">Dashboard Reseaux Sociaux</h2>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map(c => (
          <div key={c.label} className={`bg-[#151B2B] rounded-lg p-4 border-l-2 ${c.color} border border-[#2A3441]`}>
            <div className="flex items-center gap-2 mb-1">
              <c.icon className="h-4 w-4 text-[#64748b]" />
              <span className="text-[#64748b] text-xs">{c.label}</span>
            </div>
            <p className="text-white text-xl font-bold">{c.val?.toLocaleString("fr-FR") ?? 0}</p>
          </div>
        ))}
      </div>

      {/* Weekly Planner Preview */}
      <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">Planificateur Hebdomadaire</h3>
          <div className="flex gap-2 text-xs">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#0099CC]"/>Publications</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-purple-500"/>Evenements</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-500"/>Boostes</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#64748b]"/>Brouillons</span>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-2">
          {dayNames.map((day, i) => {
            const d = new Date(weekStart);
            d.setDate(weekStart.getDate() + i);
            const ds = d.toISOString().split("T")[0];
            const dayItems = cal.filter(c => c.date?.startsWith(ds));
            const isToday = ds === now.toISOString().split("T")[0];
            return (
              <div key={i} className={`rounded-lg p-2 min-h-[100px] ${isToday ? "bg-[#0099CC]/10 border border-[#0099CC]/30" : "bg-[#0B0F19] border border-[#2A3441]"}`}>
                <p className={`text-xs font-medium mb-2 ${isToday ? "text-[#0099CC]" : "text-[#64748b]"}`}>{day} {d.getDate()}</p>
                {dayItems.slice(0, 3).map(it => (
                  <div key={it.id} className={`text-xs px-1.5 py-0.5 rounded mb-1 truncate ${
                    it.boosted ? "bg-red-500/20 text-red-400" :
                    it.type === "event" ? "bg-purple-500/20 text-purple-400" :
                    it.status === "draft" ? "bg-[#2A3441] text-[#64748b]" :
                    "bg-[#0099CC]/20 text-[#0099CC]"
                  }`}>{it.title}</div>
                ))}
                {dayItems.length > 3 && <p className="text-[#64748b] text-xs">+{dayItems.length - 3}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════
   B) CREATE POST TAB
   ═══════════════════════════════════════════════════════ */
function CreatePostTab({ clientId, headers, isAdmin }) {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ title: "", content: "", platforms: ["facebook"], scheduled_at: "", image_url: "", hashtags: "" });
  const [mode, setMode] = useState("list"); // list | create
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const fetchPosts = useCallback(async () => {
    if (!clientId) return;
    const r = await axios.get(`${API}/social/posts`, { headers, params: { client_id: clientId } });
    setPosts(r.data);
  }, [clientId]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const togglePlatform = (p) => {
    setForm(f => ({
      ...f,
      platforms: f.platforms.includes(p) ? f.platforms.filter(x => x !== p) : [...f.platforms, p],
    }));
  };

  const save = async (status) => {
    if (!form.title || !form.content) { toast.error("Titre et contenu requis"); return; }
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/social/posts`, {
        client_id: clientId, ...form, status: status === "published" ? "approved" : status,
        hashtags: form.hashtags ? form.hashtags.split(",").map(h => h.trim()) : [],
        platform: form.platforms[0] || "facebook",
      }, { headers });
      if (status === "published" && resp.data?.id) {
        try {
          const pubResp = await axios.post(`${API}/social/posts/${resp.data.id}/publish`, {}, { headers });
          const results = pubResp.data?.results || {};
          const successes = Object.entries(results).filter(([, r]) => r.success).map(([p]) => p);
          const failures = Object.entries(results).filter(([, r]) => !r.success).map(([p, r]) => `${p}: ${r.error}`);
          if (successes.length > 0) toast.success(`Publie sur ${successes.join(", ")} !`);
          if (failures.length > 0) toast.error(failures.join("\n"));
        } catch (e) { toast.error("Post cree mais erreur de publication: " + (e.response?.data?.detail || e.message)); }
      } else {
        toast.success(status === "draft" ? "Brouillon enregistre" : "Post pret a publier");
      }
      setMode("list");
      setForm({ title: "", content: "", platforms: ["facebook"], scheduled_at: "", image_url: "", hashtags: "" });
      fetchPosts();
    } catch (e) { toast.error("Erreur: " + (e.response?.data?.detail || e.message)); }
    setLoading(false);
  };

  const publishPost = async (id) => {
    try {
      const resp = await axios.post(`${API}/social/posts/${id}/publish`, {}, { headers });
      const results = resp.data?.results || {};
      const successes = Object.entries(results).filter(([, r]) => r.success).map(([p]) => p);
      const failures = Object.entries(results).filter(([, r]) => !r.success).map(([p, r]) => `${p}: ${r.error}`);
      if (successes.length > 0) toast.success(`Publie sur ${successes.join(", ")} !`);
      if (failures.length > 0) toast.error(failures.join("\n"));
      fetchPosts();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de publication"); }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Supprimer ce post ?")) return;
    await axios.delete(`${API}/social/posts/${id}`, { headers });
    toast.success("Supprime");
    fetchPosts();
  };

  if (mode === "create") return (
    <div className="space-y-5" data-testid="create-post-form">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => setMode("list")} size="sm"><ChevronLeft className="h-4 w-4" /></Button>
        <h2 className="text-xl font-bold text-white">Creer une publication</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-4 bg-[#151B2B] rounded-xl border border-[#2A3441] p-5">
          <div>
            <label className="text-[#94A3B8] text-sm mb-1 block">Titre</label>
            <input value={form.title} onChange={e => set("title", e.target.value)}
              className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm" data-testid="post-title" />
          </div>
          <div>
            <label className="text-[#94A3B8] text-sm mb-1 block">Contenu du post</label>
            <textarea value={form.content} onChange={e => set("content", e.target.value)} rows={5}
              className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm resize-none" data-testid="post-content" />
            <p className="text-[#64748b] text-xs mt-1">{form.content.length}/280 caracteres</p>
          </div>
          <div>
            <label className="text-[#94A3B8] text-sm mb-1 block">Plateformes</label>
            <div className="flex flex-wrap gap-2">
              {ALL_PLATFORMS.map(pk => (
                <button key={pk} onClick={() => togglePlatform(pk)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    form.platforms.includes(pk) ? "border-transparent text-white" : "border-[#2A3441] text-[#64748b] hover:text-white"
                  }`}
                  style={form.platforms.includes(pk) ? { backgroundColor: P[pk].color } : {}}
                  data-testid={`platform-toggle-${pk}`}>
                  {P[pk].label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[#94A3B8] text-sm mb-1 block">URL image (optionnel)</label>
            <input value={form.image_url} onChange={e => set("image_url", e.target.value)} placeholder="https://..."
              className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[#94A3B8] text-sm mb-1 block">Hashtags (separes par ,)</label>
            <input value={form.hashtags} onChange={e => set("hashtags", e.target.value)} placeholder="#menuisier, #vitrier"
              className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm" data-testid="post-hashtags" />
          </div>
          <div>
            <label className="text-[#94A3B8] text-sm mb-1 block">Programmer (optionnel)</label>
            <input type="datetime-local" value={form.scheduled_at} onChange={e => set("scheduled_at", e.target.value)}
              className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm" data-testid="post-schedule" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button onClick={() => save("draft")} variant="outline" className="flex-1 border-[#2A3441] text-[#94A3B8]" data-testid="save-draft-btn">
              <Save className="h-4 w-4 mr-2" /> Brouillon
            </Button>
            <Button onClick={() => save("published")} className="flex-1 bg-[#0099CC] hover:bg-[#0099CC]/80" data-testid="publish-btn">
              <Send className="h-4 w-4 mr-2" /> Publier
            </Button>
          </div>
        </div>

        {/* Live Preview */}
        <div className="space-y-4">
          <h3 className="text-white font-semibold">Apercu</h3>
          {form.platforms.map(pk => (
            <div key={pk} className="bg-[#151B2B] rounded-xl border border-[#2A3441] overflow-hidden" data-testid={`preview-${pk}`}>
              <div className="px-4 py-2 border-b border-[#2A3441] flex items-center gap-2">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: P[pk].color }} />
                <span className="text-white text-xs font-medium">{P[pk].label}</span>
              </div>
              <div className="p-4">
                {form.image_url && <div className="w-full h-40 bg-[#0B0F19] rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                  <img src={form.image_url} alt="" className="max-h-40 object-cover" onError={e => e.target.style.display='none'} />
                </div>}
                <p className="text-white text-sm mb-2">{form.content || "Votre texte apparaitra ici..."}</p>
                {form.hashtags && <p className="text-[#0099CC] text-xs">{form.hashtags.split(",").map(h => h.trim().startsWith("#") ? h.trim() : `#${h.trim()}`).join(" ")}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // List mode
  return (
    <div className="space-y-5" data-testid="posts-list">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Publications</h2>
        <Button onClick={() => setMode("create")} className="bg-[#0099CC] hover:bg-[#0099CC]/80" data-testid="new-post-btn">
          <Plus className="h-4 w-4 mr-2" /> Nouvelle publication
        </Button>
      </div>
      {posts.length === 0 && <p className="text-[#64748b] text-center py-12">Aucune publication</p>}
      <div className="space-y-3">
        {posts.map(p => (
          <div key={p.id} className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-4 flex items-center gap-4" data-testid={`post-${p.id}`}>
            <div className="flex gap-1">
              {(p.platforms || [p.platform]).map(pk => (
                <span key={pk} className="h-6 w-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                  style={{ backgroundColor: P[pk]?.color || "#666" }}>{P[pk]?.short || "?"}</span>
              ))}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm truncate">{p.title || "Sans titre"}</p>
              <p className="text-[#64748b] text-xs truncate">{p.content?.substring(0, 80)}</p>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[p.status] || ""}`}>
              {STATUS_LABELS[p.status] || p.status}
            </span>
            {p.boost && <Rocket className="h-4 w-4 text-red-400" />}
            {p.scheduled_at && <Clock className="h-4 w-4 text-yellow-400" />}
            {isAdmin && p.status !== "published" && (
              <button onClick={() => publishPost(p.id)} className="text-green-400 hover:text-green-300 transition-colors" title="Publier sur les reseaux" data-testid={`publish-post-${p.id}`}>
                <Send className="h-4 w-4" />
              </button>
            )}
            {isAdmin && <button onClick={() => deletePost(p.id)} className="text-[#64748b] hover:text-red-400"><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════
   C) CREATE EVENT TAB
   ═══════════════════════════════════════════════════════ */
function CreateEventTab({ clientId, headers }) {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", start_date: "", start_time: "", end_date: "", end_time: "", all_day: false, platforms: ["google_business"] });
  const [mode, setMode] = useState("list");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const fetchEvents = useCallback(async () => {
    if (!clientId) return;
    const r = await axios.get(`${API}/social/events`, { headers, params: { client_id: clientId } });
    setEvents(r.data);
  }, [clientId]);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const save = async (status) => {
    if (!form.title) { toast.error("Titre requis"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/social/events`, { client_id: clientId, ...form, status }, { headers });
      toast.success(status === "published" ? "Evenement publie" : "Brouillon enregistre");
      setMode("list");
      setForm({ title: "", description: "", start_date: "", start_time: "", end_date: "", end_time: "", all_day: false, platforms: ["google_business"] });
      fetchEvents();
    } catch (e) { toast.error("Erreur"); }
    setLoading(false);
  };

  if (mode === "create") return (
    <div className="space-y-5" data-testid="create-event-form">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => setMode("list")} size="sm"><ChevronLeft className="h-4 w-4" /></Button>
        <h2 className="text-xl font-bold text-white">Creer un evenement</h2>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4 bg-[#151B2B] rounded-xl border border-[#2A3441] p-5">
          <div>
            <label className="text-[#94A3B8] text-sm mb-1 block">Titre de l'evenement</label>
            <input value={form.title} onChange={e => set("title", e.target.value)}
              className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm" data-testid="event-title" />
          </div>
          <div>
            <label className="text-[#94A3B8] text-sm mb-1 block">Description</label>
            <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={4}
              className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm resize-none" data-testid="event-desc" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.all_day} onChange={e => set("all_day", e.target.checked)}
              className="rounded border-[#2A3441]" />
            <span className="text-[#94A3B8] text-sm">Journee entiere</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[#94A3B8] text-sm mb-1 block">Date debut</label>
              <input type="date" value={form.start_date} onChange={e => set("start_date", e.target.value)}
                className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm" data-testid="event-start-date" />
            </div>
            {!form.all_day && <div>
              <label className="text-[#94A3B8] text-sm mb-1 block">Heure debut</label>
              <input type="time" value={form.start_time} onChange={e => set("start_time", e.target.value)}
                className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm" />
            </div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[#94A3B8] text-sm mb-1 block">Date fin (optionnel)</label>
              <input type="date" value={form.end_date} onChange={e => set("end_date", e.target.value)}
                className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm" />
            </div>
            {!form.all_day && <div>
              <label className="text-[#94A3B8] text-sm mb-1 block">Heure fin (optionnel)</label>
              <input type="time" value={form.end_time} onChange={e => set("end_time", e.target.value)}
                className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm" />
            </div>}
          </div>
          <div className="flex gap-3 pt-2">
            <Button onClick={() => save("draft")} variant="outline" className="flex-1 border-[#2A3441] text-[#94A3B8]" data-testid="event-save-draft">
              <Save className="h-4 w-4 mr-2" /> Brouillon
            </Button>
            <Button onClick={() => save("published")} className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="event-publish">
              <Send className="h-4 w-4 mr-2" /> Publier
            </Button>
          </div>
        </div>
        {/* Preview Google Business style */}
        <div data-testid="event-preview">
          <h3 className="text-white font-semibold mb-3">Apercu Google Business</h3>
          <div className="bg-white rounded-xl p-5 text-black">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-8 w-8 rounded-full bg-[#4285F4] flex items-center justify-center text-white text-xs font-bold">G</div>
              <span className="font-medium text-sm">Google Business Profile</span>
            </div>
            <h4 className="font-bold text-base mb-1">{form.title || "Titre de l'evenement"}</h4>
            <p className="text-gray-500 text-xs mb-2">
              {form.start_date ? new Date(form.start_date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }) : "Date a definir"}
              {form.start_time && !form.all_day ? ` a ${form.start_time}` : form.all_day ? " - Journee entiere" : ""}
            </p>
            <p className="text-gray-700 text-sm">{form.description || "Description de l'evenement..."}</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="events-list">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Evenements</h2>
        <Button onClick={() => setMode("create")} className="bg-purple-600 hover:bg-purple-700" data-testid="new-event-btn">
          <Plus className="h-4 w-4 mr-2" /> Nouvel evenement
        </Button>
      </div>
      {events.length === 0 && <p className="text-[#64748b] text-center py-12">Aucun evenement</p>}
      <div className="space-y-3">
        {events.map(e => (
          <div key={e.id} className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-4 flex items-center gap-4">
            <CalendarDays className="h-5 w-5 text-purple-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm">{e.title}</p>
              <p className="text-[#64748b] text-xs">{e.start_date} {e.start_time && `a ${e.start_time}`}</p>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[e.status] || ""}`}>{STATUS_LABELS[e.status] || e.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════
   D) SERIES TAB (Wizard)
   ═══════════════════════════════════════════════════════ */
function SeriesTab({ clientId, headers }) {
  const [seriesList, setSeriesList] = useState([]);
  const [step, setStep] = useState(0); // 0=list, 1-4=wizard steps
  const [form, setForm] = useState({ topic: "", platforms: ["facebook"], post_count: 5, frequency: "weekly", start_date: "" });
  const [generated, setGenerated] = useState([]);
  const [selected, setSelected] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [seriesId, setSeriesId] = useState(null);

  const fetchSeries = useCallback(async () => {
    if (!clientId) return;
    const r = await axios.get(`${API}/social/series`, { headers, params: { client_id: clientId } });
    setSeriesList(r.data);
  }, [clientId]);
  useEffect(() => { fetchSeries(); }, [fetchSeries]);

  const togglePlatform = (p) => setForm(f => ({ ...f, platforms: f.platforms.includes(p) ? f.platforms.filter(x => x !== p) : [...f.platforms, p] }));

  const createAndGenerate = async () => {
    if (!form.topic) { toast.error("Sujet requis"); return; }
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/social/series`, { client_id: clientId, ...form }, { headers });
      setSeriesId(r.data.id);
      const g = await axios.post(`${API}/social/series/${r.data.id}/generate`, {}, { headers });
      const posts = g.data.posts || [];
      setGenerated(posts);
      setSelected(posts.map((_, i) => i));
      setStep(3);
    } catch (e) { toast.error("Erreur generation IA"); }
    setGenerating(false);
  };

  const scheduleSeries = async () => {
    try {
      await axios.post(`${API}/social/series/${seriesId}/schedule`, {}, { headers });
      toast.success("Serie planifiee !");
      setStep(0);
      fetchSeries();
    } catch (e) { toast.error("Erreur"); }
  };

  if (step === 0) return (
    <div className="space-y-5" data-testid="series-list">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Series de publications</h2>
        <Button onClick={() => { setStep(1); setForm({ topic: "", platforms: ["facebook"], post_count: 5, frequency: "weekly", start_date: "" }); }}
          className="bg-[#0099CC] hover:bg-[#0099CC]/80" data-testid="new-series-btn">
          <Layers className="h-4 w-4 mr-2" /> Nouvelle serie
        </Button>
      </div>
      {seriesList.length === 0 && <p className="text-[#64748b] text-center py-12">Aucune serie</p>}
      {seriesList.map(s => (
        <div key={s.id} className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-4 flex items-center gap-4">
          <Layers className="h-5 w-5 text-[#0099CC]" />
          <div className="flex-1"><p className="text-white text-sm font-medium">{s.topic}</p><p className="text-[#64748b] text-xs">{s.post_count} posts | {s.frequency}</p></div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[s.status] || ""}`}>{STATUS_LABELS[s.status] || s.status}</span>
        </div>
      ))}
    </div>
  );

  // Wizard Steps
  const stepTitles = ["", "Sujet & Plateformes", "Parametres", "Apercu IA", "Confirmation"];
  return (
    <div className="space-y-5" data-testid="series-wizard">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => setStep(s => Math.max(0, s - 1))} size="sm"><ChevronLeft className="h-4 w-4" /></Button>
        <h2 className="text-xl font-bold text-white">Serie — Etape {step}/4 : {stepTitles[step]}</h2>
      </div>
      {/* Progress bar */}
      <div className="flex gap-1">{[1,2,3,4].map(i => <div key={i} className={`h-1 flex-1 rounded ${i <= step ? "bg-[#0099CC]" : "bg-[#2A3441]"}`} />)}</div>

      {step === 1 && (
        <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-5 space-y-4 max-w-lg">
          <div><label className="text-[#94A3B8] text-sm mb-1 block">Sujet de la serie</label>
            <textarea value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))} rows={3} maxLength={500}
              placeholder="Ex: Conseils menuiserie pour particuliers" className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm resize-none" data-testid="series-topic" />
            <p className="text-[#64748b] text-xs mt-1">{form.topic.length}/500</p>
          </div>
          <div><label className="text-[#94A3B8] text-sm mb-1 block">Plateformes</label>
            <div className="flex flex-wrap gap-2">
              {ALL_PLATFORMS.map(pk => (
                <button key={pk} onClick={() => togglePlatform(pk)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    form.platforms.includes(pk) ? "border-transparent text-white" : "border-[#2A3441] text-[#64748b]"
                  }`} style={form.platforms.includes(pk) ? { backgroundColor: P[pk].color } : {}}>
                  {P[pk].label}
                </button>
              ))}
            </div>
          </div>
          <Button onClick={() => setStep(2)} className="w-full bg-[#0099CC]" disabled={!form.topic}>Suivant</Button>
        </div>
      )}

      {step === 2 && (
        <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-5 space-y-4 max-w-lg">
          <div><label className="text-[#94A3B8] text-sm mb-1 block">Nombre de posts</label>
            <input type="number" min={2} max={20} value={form.post_count} onChange={e => setForm(f => ({ ...f, post_count: parseInt(e.target.value) || 5 }))}
              className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm" data-testid="series-count" />
          </div>
          <div><label className="text-[#94A3B8] text-sm mb-1 block">Frequence</label>
            <select value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}
              className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm">
              <option value="daily">Quotidien</option><option value="weekly">Hebdomadaire</option><option value="monthly">Mensuel</option>
            </select>
          </div>
          <div><label className="text-[#94A3B8] text-sm mb-1 block">Date de debut</label>
            <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
              className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm" data-testid="series-start" />
          </div>
          <Button onClick={createAndGenerate} className="w-full bg-[#0099CC]" disabled={generating}>
            {generating ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Generation IA...</> : <><Zap className="h-4 w-4 mr-2" /> Generer les posts</>}
          </Button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <p className="text-[#94A3B8] text-sm">{generated.length} posts generes. Selectionnez ceux a garder :</p>
          {generated.map((g, i) => (
            <div key={i} className={`bg-[#151B2B] rounded-xl border p-4 cursor-pointer transition-all ${
              selected.includes(i) ? "border-[#0099CC]" : "border-[#2A3441] opacity-50"
            }`} onClick={() => setSelected(s => s.includes(i) ? s.filter(x => x !== i) : [...s, i])} data-testid={`series-post-${i}`}>
              <p className="text-white font-medium text-sm">{g.title}</p>
              <p className="text-[#94A3B8] text-xs mt-1">{g.content}</p>
              {g.hashtags && <p className="text-[#0099CC] text-xs mt-1">{g.hashtags.join(" ")}</p>}
            </div>
          ))}
          <Button onClick={() => setStep(4)} className="bg-[#0099CC]" disabled={selected.length === 0}>
            Suivant ({selected.length} selectionnes)
          </Button>
        </div>
      )}

      {step === 4 && (
        <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-5 space-y-4 max-w-lg">
          <h3 className="text-white font-semibold">Confirmation</h3>
          <p className="text-[#94A3B8] text-sm">Sujet : {form.topic}</p>
          <p className="text-[#94A3B8] text-sm">Posts : {selected.length} | Frequence : {form.frequency}</p>
          <p className="text-[#94A3B8] text-sm">Plateformes : {form.platforms.map(p => P[p].label).join(", ")}</p>
          <Button onClick={scheduleSeries} className="w-full bg-green-600 hover:bg-green-700" data-testid="confirm-series-btn">
            Confirmer et planifier
          </Button>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════
   E) BOOST TAB
   ═══════════════════════════════════════════════════════ */
function BoostTab({ clientId, headers }) {
  const [posts, setPosts] = useState([]);
  const [boosted, setBoosted] = useState([]);
  const [boostForm, setBoostForm] = useState(null); // { postId, budget, duration_days, audience }

  const fetchData = useCallback(async () => {
    if (!clientId) return;
    const [p, b] = await Promise.all([
      axios.get(`${API}/social/posts`, { headers, params: { client_id: clientId } }),
      axios.get(`${API}/social/boosts`, { headers, params: { client_id: clientId } }),
    ]);
    setPosts(p.data.filter(x => x.status === "published"));
    setBoosted(b.data);
  }, [clientId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const submitBoost = async () => {
    try {
      await axios.post(`${API}/social/posts/${boostForm.postId}/boost`, {
        budget: boostForm.budget, duration_days: boostForm.duration_days, audience: boostForm.audience,
      }, { headers });
      toast.success("Boost configure !");
      setBoostForm(null);
      fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-5" data-testid="boost-tab">
      <h2 className="text-xl font-bold text-white">Booster une publication</h2>

      {boosted.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-[#94A3B8] text-sm font-medium">Publications boostees</h3>
          {boosted.map(b => (
            <div key={b.id} className="bg-[#151B2B] rounded-lg border border-red-500/30 p-3 flex items-center gap-3">
              <Rocket className="h-4 w-4 text-red-400" />
              <div className="flex-1"><p className="text-white text-sm">{b.title}</p></div>
              <span className="text-red-400 text-xs">{b.boost?.budget}EUR / {b.boost?.duration_days}j</span>
            </div>
          ))}
        </div>
      )}

      <h3 className="text-[#94A3B8] text-sm font-medium">Publications publiees</h3>
      {posts.length === 0 && <p className="text-[#64748b] text-center py-8">Aucune publication publiee a booster</p>}
      {posts.filter(p => !p.boost).map(p => (
        <div key={p.id} className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-4 flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium text-sm">{p.title}</p>
            <p className="text-[#64748b] text-xs truncate">{p.content?.substring(0, 80)}</p>
          </div>
          <Button size="sm" onClick={() => setBoostForm({ postId: p.id, budget: 50, duration_days: 7, audience: "local" })}
            className="bg-red-600 hover:bg-red-700 text-xs" data-testid={`boost-${p.id}`}>
            <Rocket className="h-3 w-3 mr-1" /> Booster
          </Button>
        </div>
      ))}

      {/* Boost modal */}
      {boostForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-6 w-full max-w-sm mx-4 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2"><Rocket className="h-5 w-5 text-red-400" /> Configurer le boost</h3>
            <div><label className="text-[#94A3B8] text-sm">Budget (EUR)</label>
              <input type="number" value={boostForm.budget} onChange={e => setBoostForm(f => ({ ...f, budget: parseInt(e.target.value) || 0 }))}
                className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm mt-1" data-testid="boost-budget" />
            </div>
            <div><label className="text-[#94A3B8] text-sm">Duree (jours)</label>
              <input type="number" value={boostForm.duration_days} onChange={e => setBoostForm(f => ({ ...f, duration_days: parseInt(e.target.value) || 7 }))}
                className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm mt-1" />
            </div>
            <div><label className="text-[#94A3B8] text-sm">Audience</label>
              <select value={boostForm.audience} onChange={e => setBoostForm(f => ({ ...f, audience: e.target.value }))}
                className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm mt-1">
                <option value="local">Locale (ville)</option><option value="regional">Regionale</option><option value="national">Nationale</option>
              </select>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => setBoostForm(null)} variant="outline" className="flex-1 border-[#2A3441] text-[#94A3B8]">Annuler</Button>
              <Button onClick={submitBoost} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-boost-btn">Confirmer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════
   G) PLANNER TAB (Monthly Calendar)
   ═══════════════════════════════════════════════════════ */
function PlannerTab({ clientId, headers }) {
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState(new Date());

  const fetchCal = useCallback(async () => {
    if (!clientId) return;
    const r = await axios.get(`${API}/social/calendar`, { headers, params: { client_id: clientId } });
    setItems(r.data);
  }, [clientId]);
  useEffect(() => { fetchCal(); }, [fetchCal]);

  const y = month.getFullYear(), m = month.getMonth();
  const firstDay = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const offset = firstDay === 0 ? 6 : firstDay - 1; // Monday start
  const today = new Date().toISOString().split("T")[0];

  const prev = () => setMonth(new Date(y, m - 1, 1));
  const next = () => setMonth(new Date(y, m + 1, 1));

  return (
    <div className="space-y-5" data-testid="planner-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Planificateur</h2>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={prev}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-white font-medium min-w-[140px] text-center">
            {month.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
          </span>
          <Button variant="ghost" size="sm" onClick={next}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-[#2A3441]">
          {["Lun","Mar","Mer","Jeu","Ven","Sam","Dim"].map(d => (
            <div key={d} className="px-2 py-2 text-[#64748b] text-xs font-medium text-center">{d}</div>
          ))}
        </div>
        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {Array.from({ length: 42 }).map((_, i) => {
            const day = i - offset + 1;
            const isValid = day >= 1 && day <= daysInMonth;
            const ds = isValid ? `${y}-${String(m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}` : "";
            const dayItems = items.filter(it => it.date?.startsWith(ds));
            const isToday = ds === today;

            return (
              <div key={i} className={`min-h-[80px] border-b border-r border-[#2A3441]/50 p-1.5 ${!isValid ? "bg-[#0B0F19]/30" : ""} ${isToday ? "bg-[#0099CC]/5" : ""}`}>
                {isValid && (
                  <>
                    <p className={`text-xs mb-1 ${isToday ? "text-[#0099CC] font-bold" : "text-[#64748b]"}`}>{day}</p>
                    {dayItems.slice(0, 3).map(it => (
                      <div key={it.id} className={`text-[10px] px-1 py-0.5 rounded mb-0.5 truncate cursor-default ${
                        it.boosted ? "bg-red-500/20 text-red-400" :
                        it.type === "event" ? "bg-purple-500/20 text-purple-400" :
                        it.status === "draft" ? "bg-[#2A3441] text-[#64748b]" :
                        "bg-[#0099CC]/20 text-[#0099CC]"
                      }`} title={it.title}>{it.title}</div>
                    ))}
                    {dayItems.length > 3 && <p className="text-[#64748b] text-[10px]">+{dayItems.length - 3}</p>}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-[#94A3B8]">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#0099CC]"/>Publications</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-purple-500"/>Evenements</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-red-500"/>Boostes</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#64748b]"/>Brouillons</span>
      </div>
    </div>
  );
}
